/**
 * useGetPaidEntryEligibility — "can this viewer enter paid competitions from
 * where they are", asked ONCE and read by every surface that offers entry.
 *
 * 37 surfaces offer entry. The per-competition check
 * ({@link useCompetitionEligibility}) needs a board id, so using it on a browse
 * grid would fire one request per card to get the same answer every time.
 * Location and age do not vary by board, so they are asked once here; what does
 * vary — the Connecticut and Maryland prize caps — stays on the per-competition
 * check, which the detail pages still run.
 *
 * Anchor: devourplay-anchor/Competitions/competitions-browse-v8.html
 * ("Simulate: viewer in Louisiana") — the reason is said ONCE at the top, the
 * boards stay visible, and no CTA offers what it cannot do.
 */

import {useQuery} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import {CompetitionsApi, JurisdictionBlockReason, PaidEntryEligibilityResponse} from "@devour/client";
import {IStore} from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";

export const PAID_ENTRY_ELIGIBILITY_QUERY_KEY = "paid-entry-eligibility";

export interface PaidEntryEligibility {
    /** True only when the viewer is genuinely refused. Never true while loading or on error. */
    isBlocked: boolean;
    /**
     * Where the viewer is, for player-facing copy — the region name when the
     * provider gave one, otherwise the code, otherwise the country. Non-empty
     * whenever the check answered at all, so copy never renders "available in
     * undefined". Populated even when NOT blocked: a per-board prize cap has
     * nothing else to name the region with.
     */
    placeLabel: string;
    /**
     * The refusal in CTA-length words, already branched on the cause. Every
     * surface renders this verbatim — a VPN block is not a location block, and
     * every surface that built its own "Not available in <place>" told a player
     * on a VPN to move house.
     */
    blockedLabel: string;
    /** Structured cause, for surfaces that need to say more than the chip does. */
    blockReason?: JurisdictionBlockReason;
    /** Operator-written reason from the policy, when there is one. */
    reason?: string;
}

/** Nothing to say while we do not know. */
const NOT_BLOCKED: PaidEntryEligibility = {isBlocked: false, placeLabel: "", blockedLabel: ""};

function buildBlockedLabel(blockReason: JurisdictionBlockReason | undefined, placeLabel: string): string {
    if (blockReason === JurisdictionBlockReason.ANONYMISED_CONNECTION) {
        return "Turn off your VPN";
    }
    if (blockReason === JurisdictionBlockReason.UNKNOWN_LOCATION) {
        return "Location not confirmed";
    }
    return `Not available in ${placeLabel}`;
}

export function useGetPaidEntryEligibility(): PaidEntryEligibility {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    const {data} = useQuery({
        queryKey: [PAID_ENTRY_ELIGIBILITY_QUERY_KEY],
        queryFn: async (): Promise<PaidEntryEligibilityResponse> =>
            new CompetitionsApi(getConfig(fullToken)).getCompetitionsEligibilityPaidEntry(),
        /* userAuthentication — an anonymous viewer has no answer to fetch. */
        enabled: Boolean(fullToken),
        /*
         * Once per session. The answer moves only when the viewer's location or
         * age does, and re-asking on every mount would put a request behind every
         * rail and grid on the page — the cost this hook exists to remove.
         */
        staleTime: Infinity,
        retry: 1,
    });

    /*
     * A failure to ask is NOT a refusal. If the endpoint is down or the viewer is
     * signed out, every CTA behaves exactly as it did before this existed and the
     * backend still refuses at join. Blocking on a network error would take entry
     * away from everyone the moment one route went down.
     */
    if (!data) {
        return NOT_BLOCKED;
    }

    const placeLabel = data.regionName || data.region || data.country || "your location";

    /*
     * The place is carried even when this check PASSES. A board can still
     * refuse on its own prize cap, and that refusal has only the viewer-level
     * answer to name a region with — without this it said "your location" to a
     * player we had already resolved to Maryland.
     */
    if (data.allowed !== false) {
        return {isBlocked: false, placeLabel, blockedLabel: ""};
    }

    return {
        isBlocked: true,
        placeLabel,
        blockedLabel: buildBlockedLabel(data.blockReason, placeLabel),
        blockReason: data.blockReason,
        reason: data.reason,
    };
}
